import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import './ImportReceptions.scss';
import Menu from '../../components/Menu/Menu';
import MobileMenu from '../../components/Menu/MobileMenu';
import { importService } from "../../services/ImportService";
import AttachementField from "../../components/AttachementField/AttachementField.jsx";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from "../../components/Header/Header.jsx";
import { FILE_STATUS_FILTERS, FILE_STATUS_LABELS, MAX_RECENT_ERRORS, clampPercent, normalizeFileStatus, buildEmptyFileState, getDerivedProgress } from './importReceptionsStreamUtils';

const ImportReceptions = () => {
    const importAbortControllerRef = useRef(null);

    const [receiptsFiles, setReceiptsFiles] = useState([]);
    const [disabledReceipts, setDisabledReceipts] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentBatch, setCurrentBatch] = useState(null);
    const [recentErrors, setRecentErrors] = useState([]);
    const [filesByName, setFilesByName] = useState({});
    const [importSummary, setImportSummary] = useState({
        jobId: '',
        totalFiles: 0,
        processedFiles: 0,
        done: false,
        globalCounts: {}
    });

    const hasImportResults = useMemo(() => {
        const hasFiles = Object.keys(filesByName).length > 0;
        const hasErrors = recentErrors.length > 0;
        const hasGlobalCounts = Object.values(importSummary.globalCounts || {}).some((value) => Number(value) > 0);
        const hasMeta = Number(importSummary.totalFiles) > 0 || Number(importSummary.processedFiles) > 0;
        return hasFiles || hasErrors || hasGlobalCounts || hasMeta;
    }, [filesByName, recentErrors, importSummary]);

    const showLivePanel = isImporting || hasImportResults;

    useEffect(() => () => {
        if (importAbortControllerRef.current) {
            importAbortControllerRef.current.abort();
            importAbortControllerRef.current = null;
        }
    }, []);

    const updateFileEntry = (fileName, updater) => {
        if (!fileName) return;

        setFilesByName((prev) => {
            const previousFileState = prev[fileName] || buildEmptyFileState(fileName);
            const nextFileState = typeof updater === 'function'
                ? updater(previousFileState)
                : { ...previousFileState, ...updater };

            return { ...prev, [fileName]: nextFileState };
        });
    };

    const applyFinalFilesPayload = (filesPayload = []) => {
        if (!Array.isArray(filesPayload) || filesPayload.length === 0) return;

        setFilesByName((prev) => {
            const nextState = { ...prev };

            filesPayload.forEach((fileEntry) => {
                const fileName = fileEntry?.file_name || fileEntry?.fileName;
                if (!fileName) return;

                const previousFileState = nextState[fileName] || buildEmptyFileState(fileName);
                nextState[fileName] = {
                    ...previousFileState,
                    ...fileEntry,
                    fileName,
                    status: normalizeFileStatus(fileEntry?.status || previousFileState.status),
                    progress: 100,
                    counts: fileEntry?.counts || previousFileState.counts || {},
                    errors: Array.isArray(fileEntry?.errors) ? fileEntry.errors : previousFileState.errors || [],
                    unmatchedProducts: Array.isArray(fileEntry?.unmatched_products)
                        ? fileEntry.unmatched_products
                        : previousFileState.unmatchedProducts || []
                };
            });

            return nextState;
        });
    };

    const handleStreamPayload = ({ event, data }) => {
        if (!data || typeof data !== 'object') return;

        if (data.counts && typeof data.counts === 'object') {
            setImportSummary((prev) => ({
                ...prev,
                globalCounts: { ...prev.globalCounts, ...data.counts }
            }));
        }

        switch ((event || '').toLowerCase()) {
            case 'import_started':
                setImportSummary((prev) => ({
                    ...prev,
                    jobId: data.job_id || prev.jobId,
                    totalFiles: data.total_files ?? prev.totalFiles,
                    processedFiles: data.processed_files ?? prev.processedFiles,
                    done: false
                }));
                break;
            case 'batch_started':
                setCurrentBatch({
                    index: data.batch_index || data.batch || 1,
                    total: data.total_batches || data.batches || 1
                });
                break;
            case 'file_started': {
                const fileName = data.file_name || data.fileName;
                updateFileEntry(fileName, (previousFileState) => ({
                    ...previousFileState,
                    status: 'in_progress',
                    progress: 0
                }));
                break;
            }
            case 'file_progress': {
                const fileName = data.file_name || data.fileName;
                updateFileEntry(fileName, (previousFileState) => ({
                    ...previousFileState,
                    status: normalizeFileStatus(data.status || previousFileState.status),
                    progress: getDerivedProgress(data, previousFileState.progress),
                    counts: data.counts || previousFileState.counts,
                    recentRows: Array.isArray(data.recent_rows) ? data.recent_rows : previousFileState.recentRows,
                    recentErrors: Array.isArray(data.recent_errors) ? data.recent_errors : previousFileState.recentErrors,
                    unmatchedProducts: Array.isArray(data.unmatched_products)
                        ? data.unmatched_products
                        : previousFileState.unmatchedProducts
                }));

                if (Array.isArray(data.recent_errors) && data.recent_errors.length > 0) {
                    setRecentErrors((previousErrors) => {
                        const mappedErrors = data.recent_errors.map((errorEntry) => ({
                            fileName: fileName || 'UNKNOWN',
                            rowNumber: errorEntry?.row_number,
                            message: errorEntry?.message || 'Import error'
                        }));
                        return [...mappedErrors, ...previousErrors].slice(0, MAX_RECENT_ERRORS);
                    });
                }
                break;
            }
            case 'file_completed': {
                const fileName = data.file_name || data.fileName;
                updateFileEntry(fileName, (previousFileState) => ({
                    ...previousFileState,
                    ...data,
                    fileName,
                    status: normalizeFileStatus(data.status || previousFileState.status),
                    progress: 100,
                    counts: data.counts || previousFileState.counts,
                    errors: Array.isArray(data.errors) ? data.errors : previousFileState.errors,
                    unmatchedProducts: Array.isArray(data.unmatched_products)
                        ? data.unmatched_products
                        : previousFileState.unmatchedProducts
                }));

                setImportSummary((prev) => ({
                    ...prev,
                    processedFiles: data.processed_files ?? (prev.processedFiles + 1)
                }));
                break;
            }
            case 'batch_completed':
                if (data.counts && typeof data.counts === 'object') {
                    setImportSummary((prev) => ({
                        ...prev,
                        globalCounts: { ...prev.globalCounts, ...data.counts }
                    }));
                }
                break;
            case 'import_completed':
                applyFinalFilesPayload(data.files);
                setImportSummary((prev) => ({
                    ...prev,
                    totalFiles: data.total_files ?? prev.totalFiles,
                    processedFiles: data.processed_files ?? data.total_files ?? prev.processedFiles,
                    globalCounts: data.counts && typeof data.counts === 'object' ? data.counts : prev.globalCounts,
                    done: true
                }));
                break;
            case 'import_error':
                setRecentErrors((previousErrors) => [
                    {
                        fileName: 'GLOBAL',
                        message: data.message || 'Import failed'
                    },
                    ...previousErrors
                ].slice(0, MAX_RECENT_ERRORS));
                setImportSummary((prev) => ({ ...prev, done: true }));
                break;
            default:
                break;
        }
    };

    const handleReceiptsDrop = (documents) => {
        const invalidFiles = documents.filter(file => !isExcelFile(file));
        if (invalidFiles.length > 0) {
            toast.error("Te rog să încarci un fișier Excel.");
            return;
        }

        setReceiptsFiles(documents);
        setDisabledReceipts(true);
    };

    const removeReceipts = () => {
        setReceiptsFiles([]);
        setDisabledReceipts(false);
    };
    const isExcelFile = (file) => {
        const allowedTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];
        return allowedTypes.includes(file.type);
    };

    const handleSubmitReceipts = () => {
        if (receiptsFiles.length === 0) {
            toast.warning("Please attach receipt files before submitting.", {
                toastId: "no-receipt-files",
            });
            return;
        }

        setDisableSubmit(true);
        setIsImporting(true);
        setCurrentBatch(null);
        setRecentErrors([]);
        setFilesByName({});
        setImportSummary({
            jobId: '',
            totalFiles: receiptsFiles.length,
            processedFiles: 0,
            done: false,
            globalCounts: {}
        });

        if (importAbortControllerRef.current) {
            importAbortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        importAbortControllerRef.current = abortController;

        importService.importReceiptsWithStream(
            { files: receiptsFiles },
            {
                signal: abortController.signal,
                onEvent: handleStreamPayload,
                onDone: (payload) => {
                    if (!payload || typeof payload !== 'object') return;

                    applyFinalFilesPayload(payload.files);
                    setImportSummary((prev) => ({
                        ...prev,
                        jobId: payload.job_id || prev.jobId,
                        totalFiles: payload.total_files ?? prev.totalFiles,
                        processedFiles: payload.processed_files ?? payload.total_files ?? prev.processedFiles,
                        globalCounts: payload.counts && typeof payload.counts === 'object'
                            ? payload.counts
                            : prev.globalCounts,
                        done: true
                    }));
                }
            }
        )
            .then((payload) => {
                if (!payload) return;

                const filesFailed = Number(payload?.counts?.files_failed || 0);
                const filesPartial = Number(payload?.counts?.files_partial_success || 0);

                if (filesFailed > 0 || filesPartial > 0) {
                    toast.warning("Importul s-a finalizat cu erori/parțial.", {
                        toastId: "receipts-import-partial"
                    });
                    return;
                }

                toast.success("Receipts imported successfully.", {
                    toastId: "receipts-import-success"
                });
                setReceiptsFiles([]);
                setDisabledReceipts(false);
            })
            .catch((error) => {
                if (error?.name === 'AbortError') {
                    return;
                }
                console.error(error);
                handleApiError(error, "Failed to import receipts.");
                setImportSummary((prev) => ({ ...prev, done: true }));
            })
            .finally(() => {
                setDisableSubmit(false);
                setIsImporting(false);
                if (importAbortControllerRef.current === abortController) {
                    importAbortControllerRef.current = null;
                }
            });
    };

    const handleApiError = (error, defaultMsg = "Import failed.") => {
        const data = error?.response?.data;
        if (data) {
            toast.error(data.message || defaultMsg, {
                toastId: "api-main-error",
            });
            if (data.errors && typeof data.errors === 'object') {
                Object.entries(data.errors).forEach(([fileName, errorMessages]) => {
                    errorMessages.forEach(msg => {
                        toast.error(`${fileName}: ${msg}`, {
                            toastId: `${fileName}-${msg}`,
                        });
                    });
                });
            }
        } else {
            toast.error(defaultMsg, {
                toastId: "api-generic-error",
            });
        }
    };

    const sortedFiles = useMemo(() => {
        return Object.values(filesByName).sort((firstFile, secondFile) => {
            if (firstFile.status === 'in_progress' && secondFile.status !== 'in_progress') return -1;
            if (firstFile.status !== 'in_progress' && secondFile.status === 'in_progress') return 1;
            return (firstFile.fileName || '').localeCompare(secondFile.fileName || '');
        });
    }, [filesByName]);

    const filteredFiles = useMemo(() => {
        if (statusFilter === 'all') return sortedFiles;
        return sortedFiles.filter((fileState) => fileState.status === statusFilter);
    }, [statusFilter, sortedFiles]);

    const displayedGlobalCounts = useMemo(() => {
        return Object.entries(importSummary.globalCounts || {}).filter(([, value]) => Number(value) > 0);
    }, [importSummary.globalCounts]);

    return (
        <div className="standard import">
            <MobileMenu />
            <Header />

            <div className='standard__container'>

                <Menu />
                <div className='standard__wrapper ' style={{ padding: 24, minHeight: 280 }}>
                    <h2 className='standard__page-title'>Import receptii</h2>
                    <h4 className='standard__page-info'>
                        Actualizează sau adaugă receptii prin import de fișiere.
                    </h4>
                    <div className='import__wrapper'>
                        <div className="import__container" style={{ padding: 24, minHeight: 280 }}>
                            <div className="import__section">
                                <div className="import__upload" style={{ marginTop: 40 }}>
                                    <AttachementField
                                        attachmentLabel="Importă recepții"
                                        onDrop={handleReceiptsDrop}
                                        disabledDocuments={disabledReceipts}
                                        document={{
                                            url: receiptsFiles.map(f => f.name).join(', ')
                                        }}
                                        removeDocument={removeReceipts}
                                        documentFilename={receiptsFiles.map(f => f.name).join(', ')}
                                        multiple
                                    />
                                </div>
                                <div className='import__submit-row'>
                                    <Button
                                        type="primary"
                                        size="large"
                                        className='import__submit-button'
                                        onClick={handleSubmitReceipts}
                                        disabled={receiptsFiles.length === 0 || disableSubmit}
                                        loading={isImporting}
                                    >
                                        Importă recepții
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className='import__right-side'>
                            {showLivePanel ? (
                                <>
                                    <div className='import__live-header'>
                                        <h3>Import live recepții</h3>
                                        <span>
                                            {isImporting ? 'Se procesează...' : importSummary.done ? 'Import finalizat' : 'Așteptare rezultate'}
                                        </span>
                                    </div>

                                    <div className='import__live-meta import__live-meta--receptions'>
                                        <div>Total fișiere: <strong>{importSummary.totalFiles || 0}</strong></div>
                                        <div>Procesate: <strong>{importSummary.processedFiles || 0}</strong></div>
                                        <div>
                                            Batch: <strong>
                                                {currentBatch ? `${currentBatch.index}/${currentBatch.total}` : '-'}
                                            </strong>
                                        </div>
                                    </div>

                                    {displayedGlobalCounts.length > 0 && (
                                        <div className='import__live-counts'>
                                            {displayedGlobalCounts.map(([key, value]) => (
                                                <span key={key}>{key.replaceAll('_', ' ')}: {value}</span>
                                            ))}
                                        </div>
                                    )}

                                    <div className='import__status-filters'>
                                        {FILE_STATUS_FILTERS.map((statusKey) => (
                                            <button
                                                key={statusKey}
                                                type='button'
                                                className={`import__filter-chip ${statusFilter === statusKey ? 'active' : ''}`}
                                                onClick={() => setStatusFilter(statusKey)}
                                            >
                                                {FILE_STATUS_LABELS[statusKey]}
                                            </button>
                                        ))}
                                    </div>

                                    <div className='import__live-list'>
                                        {filteredFiles.length === 0 ? (
                                            <div className='import__empty-state'>
                                                Nu există fișiere în acest filtru încă.
                                            </div>
                                        ) : (
                                            filteredFiles.map((fileState) => (
                                                <div key={fileState.fileName} className={`import__live-row import__live-row--${fileState.status}`}>
                                                    <div className='import__live-row-main'>
                                                        <div className='import__live-row-title'>{fileState.fileName}</div>
                                                        <div className='import__live-row-source'>{FILE_STATUS_LABELS[fileState.status] || fileState.status}</div>
                                                    </div>
                                                    <div className='import__progress-track'>
                                                        <div
                                                            className='import__progress-fill'
                                                            style={{ width: `${clampPercent(fileState.progress)}%` }}
                                                        />
                                                    </div>
                                                    <div className='import__file-counts'>
                                                        {Object.keys(fileState.counts || {}).length > 0
                                                            ? Object.entries(fileState.counts).map(([key, value]) => `${key}: ${value}`).join(' • ')
                                                            : 'Se colectează statistici...'}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {recentErrors.length > 0 && (
                                        <div className='import__errors-panel'>
                                            <h4>Ultimele erori</h4>
                                            {recentErrors.map((errorEntry, index) => (
                                                <div key={`${errorEntry.fileName}-${errorEntry.rowNumber || 'row'}-${index}`} className='import__error-row'>
                                                    {errorEntry.fileName}
                                                    {errorEntry.rowNumber ? ` • rând ${errorEntry.rowNumber}` : ''}
                                                    {`: ${errorEntry.message}`}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <h3>Apasă pe butonul alăturat pentru a încărca fișierul cu recepțiile dorite.</h3>
                                    <img src='/images/upload-icon.png' alt='upload' />
                                </>
                            )}
                        </div>
                    </div>

                </div>

            </div>
        </div>
    );
};

export default ImportReceptions;
